import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { PawPrint, Heart, Loader2 } from "lucide-react";
import SEO from "../components/SEO";

/**
 * Public memorial wall — every donation dedication where show_publicly=true.
 */
export default function InMemoryOf() {
  const [data, setData] = useState({ total: 0, dedications: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api
      .get("/dedications?limit=200")
      .then((r) => {
        if (alive) setData(r.data);
      })
      .catch(() => {})
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-14" data-testid="in-memory-page">
      <SEO
        title="In Memory Of · Animal Memorial Wall"
        description="A memorial wall honoring beloved animals whose families donated to Paws on Record in their memory. Every dedication funds the fight to protect the next one."
        path="/in-memory"
      />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-10">
        <div className="lg:col-span-8">
          <div className="label-eyebrow text-red-600">Section · In Memory Of</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter mt-2 leading-[0.95]">
            They were rescued.
            <br />
            <span className="relative inline-block">
              <span className="relative z-10">They were loved.</span>
              <span className="absolute left-0 right-0 bottom-1 h-3 bg-red-600/90 -z-0" />
            </span>
          </h1>
          <p className="text-lg text-zinc-700 mt-6 max-w-2xl leading-relaxed">
            Every dedication below was made by a person who donated to Paws on Record
            in memory of an animal they loved. Their gift funds the registry that
            protects the next one.
          </p>
        </div>
        <div className="lg:col-span-4">
          <div className="border-2 border-zinc-900 bg-amber-50 p-5">
            <div className="label-eyebrow text-red-600">Total memorials</div>
            <div className="font-display font-black text-5xl tracking-tighter mt-1" data-testid="memorial-count">
              {data.total}
            </div>
            <Link
              to="/donate"
              className="mt-4 w-full bg-zinc-900 text-white hover:bg-red-600 px-4 py-3 text-xs font-bold uppercase tracking-widest inline-flex items-center justify-center gap-2 transition-colors"
              data-testid="memorial-donate-cta"
            >
              <Heart size={12} className="fill-white" /> Add a dedication
            </Link>
          </div>
        </div>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-sm text-zinc-500" data-testid="memorial-loading">
          <Loader2 size={14} className="animate-spin" /> Loading dedications…
        </div>
      )}

      {!loading && data.dedications.length === 0 && (
        <div
          className="border-2 border-dashed border-zinc-300 bg-white p-12 text-center"
          data-testid="memorial-empty"
        >
          <PawPrint size={32} className="mx-auto text-zinc-300" />
          <div className="font-display font-black text-2xl mt-4">
            Be the first to add a dedication.
          </div>
          <p className="text-sm text-zinc-600 mt-3 max-w-md mx-auto">
            Honor an animal you loved. Your donation funds the registry that protects
            the next one — and your dedication lives here.
          </p>
          <Link to="/donate" className="btn-primary mt-6 inline-flex" data-testid="memorial-empty-cta">
            <Heart size={14} className="fill-white" /> Donate in memory of an animal
          </Link>
        </div>
      )}

      {!loading && data.dedications.length > 0 && (
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
          data-testid="memorial-grid"
        >
          {data.dedications.map((d) => (
            <article
              key={d.id}
              className="border-2 border-zinc-900 bg-white p-5 hover-shadow-harsh transition-shadow flex flex-col"
              data-testid={`memorial-card-${d.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="w-9 h-9 bg-zinc-900 text-white flex items-center justify-center flex-shrink-0">
                  <PawPrint size={16} />
                </div>
                <div className="text-[10px] uppercase tracking-widest font-mono text-zinc-400">
                  {d.created_at ? new Date(d.created_at).getFullYear() : ""}
                </div>
              </div>
              <div className="label-eyebrow text-red-600 mt-4">In memory of</div>
              <div className="font-display font-black text-2xl tracking-tighter mt-1 leading-tight break-words">
                {d.pet_name || "(unnamed)"}
              </div>
              {d.message && (
                <blockquote className="border-l-4 border-red-600 pl-3 mt-3 text-sm text-zinc-700 italic leading-relaxed flex-1">
                  &ldquo;{d.message}&rdquo;
                </blockquote>
              )}
              {d.donor_name && (
                <div className="mt-4 pt-3 border-t border-zinc-200 text-[11px] uppercase tracking-widest font-mono text-zinc-500">
                  — {d.donor_name}
                </div>
              )}
            </article>
          ))}
        </div>
      )}

      <div className="mt-16 border-2 border-zinc-900 bg-zinc-900 text-white p-8 text-center">
        <div className="label-eyebrow text-red-500">Honor an animal</div>
        <h2 className="font-display font-black text-3xl tracking-tight mt-2 leading-tight">
          Your dedication keeps this registry alive.
        </h2>
        <Link
          to="/donate"
          className="mt-5 bg-red-600 hover:bg-red-700 border-2 border-red-600 px-8 py-4 text-sm font-bold uppercase tracking-widest inline-flex items-center gap-2 transition-colors"
          data-testid="memorial-footer-cta"
        >
          <Heart size={14} className="fill-white" /> Donate in memory
        </Link>
      </div>
    </div>
  );
}
